import { useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, BadgeCheck, Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import api from '@/lib/api';
import { erroDaApi } from '@/lib/erroDaApi';
import { usePode } from '@/stores/authStore';
import Preloader from '@/components/Preloader';
import LogoFornecedor from '@/components/LogoFornecedor';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { COR_FUNDO, NOME_COR } from '../cotacoes/cores';
import { CABECALHO, CELULA, LINHA, TITULO_COLUNA } from '../cotacoes/tabela';
import FormularioVinculo, { type DadosDoVinculo } from './FormularioVinculo';
import type { ContextoDaLista } from './ProdutosPage';
// O componente homônimo deste arquivo desenha o código; o tipo descreve o
// vínculo que vem da API.
import type { CodigoDoFornecedor as CodigoDeFornecedor } from './tipos';
import { NOME_FAMILIA, type ItemErp, type Vinculo, type DetalheDoProduto } from './tipos';

const numero = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 });

/**
 * O produto aberto por cima da lista: os códigos que a Condusvale usa no ERP e
 * o de-para de cada fornecedor.
 *
 * Abre por cima de propósito. Conferir o de-para é trabalho de fila — um
 * produto atrás do outro —, e navegar para uma tela nova a cada um perderia a
 * busca, o filtro de marca e a posição da lista a cada volta. O endereço muda
 * junto (`/produtos/47`), então o link direto, o F5 e o "voltar" do navegador
 * continuam valendo.
 */
export default function ProdutoModal() {
  const { produtoId } = useParams();
  const navigate = useNavigate();
  const { comInativos } = useOutletContext<ContextoDaLista>();
  const podeCadastrar = usePode('cadastro.produtos');
  const queryClient = useQueryClient();

  const [editando, setEditando] = useState<number | null>(null);
  const [criando, setCriando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  // Qual código do ERP está com o campo aberto para escrever o código do fornecedor.
  const [escrevendoCodigo, setEscrevendoCodigo] = useState<number | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['produto', produtoId],
    queryFn: async () => (await api.get<{ data: DetalheDoProduto }>(`/produtos/${produtoId}`)).data.data,
  });

  const { data: fornecedores } = useQuery({
    queryKey: ['fornecedores'],
    queryFn: async () =>
      (await api.get<{ data: { id: number; apelido: string; marca_erp: string | null }[] }>('/fornecedores')).data
        .data,
    enabled: podeCadastrar,
  });

  const recarregar = () => {
    setEditando(null);
    setCriando(false);
    setErro(null);
    queryClient.invalidateQueries({ queryKey: ['produto', produtoId] });
    // A lista atrás mostra quantos vínculos estão conferidos: ela também muda.
    queryClient.invalidateQueries({ queryKey: ['produtos'] });
  };

  const falhou = (falha: unknown, generica: string) => setErro(erroDaApi(falha, generica));

  const confirmar = useMutation({
    mutationFn: async (id: number) => (await api.post(`/vinculos/${id}/confirmar`)).data,
    onSuccess: recarregar,
    onError: (falha) => falhou(falha, 'Não foi possível confirmar o vínculo.'),
  });

  const corrigir = useMutation({
    mutationFn: async ({ id, dados }: { id: number; dados: DadosDoVinculo }) =>
      (await api.put(`/vinculos/${id}`, dados)).data,
    onSuccess: recarregar,
    onError: (falha) => falhou(falha, 'Não foi possível corrigir o vínculo.'),
  });

  const criar = useMutation({
    mutationFn: async (dados: DadosDoVinculo) =>
      (await api.post(`/produtos/${produtoId}/vinculos`, dados)).data,
    onSuccess: recarregar,
    onError: (falha) => falhou(falha, 'Não foi possível criar o vínculo.'),
  });

  const remover = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/vinculos/${id}`)).data,
    onSuccess: recarregar,
    onError: (falha) => falhou(falha, 'Não foi possível remover o vínculo.'),
  });

  const salvarCodigo = useMutation({
    mutationFn: async ({ itemId, fornecedorId, codigo }: { itemId: number; fornecedorId: number; codigo: string }) =>
      (await api.put(`/itens-erp/${itemId}/codigos`, { fornecedor_id: fornecedorId, codigo_fornecedor: codigo })).data,
    onSuccess: () => {
      setEscrevendoCodigo(null);
      recarregar();
    },
    onError: (falha) => falhou(falha, 'Não foi possível gravar o código do fornecedor.'),
  });

  const ocupado = confirmar.isPending || corrigir.isPending || criar.isPending || remover.isPending;
  const listaDeFornecedores = fornecedores ?? [];

  // O que a lista esconde, o produto também esconde: de 36 códigos do classe 2
  // de 16 mm, 10 saíram de linha e só atrapalham quem está comprando.
  const todos = data?.itens_erp ?? [];
  const itens = comInativos ? todos : todos.filter((item) => item.ativo);
  const escondidos = todos.length - itens.length;

  // Uma linha por vínculo, com o fornecedor ao lado: o de-para se lê de uma
  // vez só, em vez de um cartão por fornecedor.
  const vinculos = (data?.fornecedores ?? []).flatMap(({ fornecedor, vinculos: doFornecedor }) =>
    doFornecedor.map((vinculo) => ({ fornecedor, vinculo })),
  );

  return (
    <Dialog open onOpenChange={(abrir) => !abrir && navigate('/produtos')}>
      {/* Largura em px: neste projeto a escala de espaçamento encolhe as classes max-w-*. */}
      <DialogContent className="max-w-[1100px] p-lg">
        {isLoading && (
          <>
            <DialogTitle>Carregando o produto…</DialogTitle>
            <Preloader className="py-3xl" />
          </>
        )}

        {(isError || (!data && !isLoading)) && (
          <>
            <DialogTitle>Produto</DialogTitle>
            <p className="py-lg text-[14px] text-danger">
              Não foi possível carregar este produto. Feche e abra de novo para tentar.
            </p>
          </>
        )}

        {data && (
          <>
            <DialogTitle className="pr-3xl">{data.produto.descricao}</DialogTitle>
            <DialogDescription>
              {NOME_FAMILIA[data.produto.familia] ?? data.produto.familia}
              {data.produto.secao_mm2 ? ` · ${numero.format(data.produto.secao_mm2)} mm²` : ''}
              {data.produto.vias > 1 ? ` · ${data.produto.vias} vias` : ''}
              {` · cotado por ${data.produto.unidade_base}`}
            </DialogDescription>

            {erro && (
              <p className="mt-md rounded-md border border-danger/30 bg-danger/5 px-md py-sm text-[13px] text-danger">
                {erro}
              </p>
            )}

            <section className="mt-lg">
              <h3 className="mb-sm text-[15px] font-bold text-ink">Códigos da Condusvale</h3>

              {itens.length === 0 ? (
                <p className="rounded-lg border border-border bg-surface px-md py-md text-[13px] text-muted">
                  {escondidos > 0
                    ? 'Todos os códigos deste produto saíram de linha. Marque "mostrar fora de linha" na lista para vê-los.'
                    : 'Nenhum código do ERP caiu neste produto ainda. Ele nasceu de uma cotação; a próxima importação da tabela de itens deve trazer os códigos.'}
                </p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-border bg-surface">
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className={CABECALHO}>
                        <th className={`${TITULO_COLUNA} text-left`}>Código</th>
                        <th className={`${TITULO_COLUNA} text-left`}>Descrição no ERP</th>
                        <th className={`${TITULO_COLUNA} text-left`}>Marca</th>
                        <th className={`${TITULO_COLUNA} text-left`}>Cor</th>
                        <th className={`${TITULO_COLUNA} text-left`}>Embalagem</th>
                        <th className={`${TITULO_COLUNA} text-left`}>Código do fornecedor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itens.map((item) => (
                        <tr key={item.id} className={`${LINHA} ${item.ativo ? '' : 'text-muted'}`}>
                          <td className={`${CELULA} tabular-nums font-bold`}>
                            {item.codigo}
                            {!item.ativo && <span className="ml-xs text-[11px] font-normal">fora de linha</span>}
                          </td>
                          <td className={CELULA}>{item.descricao}</td>
                          <td className={CELULA}>{item.marca ?? '—'}</td>
                          <td className={CELULA}>
                            {item.sigla_cor ? (
                              <span className="inline-flex items-center gap-xs">
                                <span
                                  aria-hidden="true"
                                  className="inline-block size-[11px] rounded-full border border-border"
                                  style={{ background: COR_FUNDO[item.sigla_cor] ?? '#cbd5e1' }}
                                />
                                {NOME_COR[item.sigla_cor] ?? item.cor}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className={CELULA}>{item.embalagem ?? `Por ${item.unidade.toLowerCase()}`}</td>
                          <td className={CELULA}>
                            <CodigoDoFornecedor
                              item={item}
                              fornecedores={listaDeFornecedores}
                              podeCadastrar={podeCadastrar}
                              escrevendo={escrevendoCodigo === item.id}
                              ocupado={salvarCodigo.isPending}
                              aoAbrir={() => {
                                setEscrevendoCodigo(item.id);
                                setErro(null);
                              }}
                              aoFechar={() => setEscrevendoCodigo(null)}
                              aoSalvar={(fornecedorId, codigo) =>
                                salvarCodigo.mutate({ itemId: item.id, fornecedorId, codigo })
                              }
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {escondidos > 0 && itens.length > 0 && (
                <p className="mt-xs text-[12px] text-muted">
                  {escondidos === 1
                    ? '1 código fora de linha escondido — marque "mostrar fora de linha" na lista para vê-lo.'
                    : `${escondidos} códigos fora de linha escondidos — marque "mostrar fora de linha" na lista para vê-los.`}
                </p>
              )}
            </section>

            <section className="mt-lg">
              <div className="mb-sm flex flex-wrap items-center justify-between gap-sm">
                <h3 className="text-[15px] font-bold text-ink">Como cada fornecedor chama</h3>

                {podeCadastrar && !criando && (
                  <button
                    type="button"
                    onClick={() => {
                      setCriando(true);
                      setEditando(null);
                      setErro(null);
                    }}
                    className="inline-flex items-center gap-xs rounded-md border border-accent bg-accent/10 px-sm py-xs text-[12px] font-bold text-accent hover:bg-accent hover:text-white"
                  >
                    <Plus size={13} aria-hidden="true" />
                    Adicionar vínculo
                  </button>
                )}
              </div>

              <p className="mb-sm text-[12px] text-muted">
                O sistema aprende sozinho a cada proposta lida. O código é o do cabo inteiro, não o da cor: o
                fornecedor cota o produto e a divisão por cor volta no pedido. Remover um vínculo não muda cotação já
                apurada — só faz a próxima proposta voltar a pedir a ligação.
              </p>

              {criando && (
                <FormularioVinculo
                  fornecedores={listaDeFornecedores}
                  ocupado={ocupado}
                  erro={null}
                  aoSalvar={(dados) => criar.mutate(dados)}
                  aoCancelar={() => setCriando(false)}
                />
              )}

              {vinculos.length === 0 ? (
                <p className="mt-sm rounded-lg border border-border bg-surface px-md py-md text-[13px] text-muted">
                  Nenhum fornecedor cotou este cabo ainda. Assim que uma proposta for lida, o vínculo aparece aqui.
                </p>
              ) : (
                <div className="mt-sm overflow-x-auto rounded-lg border border-border bg-surface">
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className={CABECALHO}>
                        <th className={`${TITULO_COLUNA} text-left`}>Fornecedor</th>
                        <th className={`${TITULO_COLUNA} text-left`}>Código dele</th>
                        <th className={`${TITULO_COLUNA} text-left`}>Como ele chama</th>
                        <th className={`${TITULO_COLUNA} text-left`}>Embalagem</th>
                        <th className={`${TITULO_COLUNA} text-left`}>Situação</th>
                        {podeCadastrar && <th className={`${TITULO_COLUNA} text-right`} />}
                      </tr>
                    </thead>
                    <tbody>
                      {vinculos.map(({ fornecedor, vinculo }) =>
                        editando === vinculo.id ? (
                          <tr key={vinculo.id} className={LINHA}>
                            <td className={CELULA} colSpan={podeCadastrar ? 6 : 5}>
                              <FormularioVinculo
                                fornecedores={listaDeFornecedores}
                                fornecedorId={fornecedor.id}
                                vinculo={vinculo}
                                ocupado={ocupado}
                                erro={null}
                                aoSalvar={(dados) => corrigir.mutate({ id: vinculo.id, dados })}
                                aoCancelar={() => setEditando(null)}
                              />
                            </td>
                          </tr>
                        ) : (
                          <tr key={vinculo.id} className={LINHA}>
                            <td className={CELULA}>
                              <span className="inline-flex items-center gap-xs whitespace-nowrap">
                                <LogoFornecedor
                                  logoUrl={fornecedor.logo_url}
                                  apelido={fornecedor.apelido}
                                  tamanho="sm"
                                />
                                <span className="font-bold text-ink">{fornecedor.apelido}</span>
                              </span>
                            </td>
                            <td className={`${CELULA} tabular-nums`}>{vinculo.codigo_fornecedor ?? '—'}</td>
                            <td className={CELULA}>{vinculo.descricao_fornecedor}</td>
                            <td className={CELULA}>{vinculo.embalagem ?? '—'}</td>
                            <td className={CELULA}>
                              <Selo vinculo={vinculo} />
                            </td>
                            {podeCadastrar && (
                              <td className={`${CELULA} text-right`}>
                                <span className="inline-flex items-center justify-end gap-xs whitespace-nowrap">
                                  {!vinculo.confirmado_em && (
                                    <button
                                      type="button"
                                      disabled={ocupado}
                                      onClick={() => confirmar.mutate(vinculo.id)}
                                      className="inline-flex items-center gap-xs rounded-md border border-border bg-dominant px-sm py-xs text-[12px] font-bold text-ink-soft hover:border-accent disabled:opacity-40"
                                    >
                                      <Check size={13} aria-hidden="true" />
                                      Está certo
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    disabled={ocupado}
                                    title="Corrigir"
                                    aria-label={`Corrigir o vínculo da ${fornecedor.apelido}`}
                                    onClick={() => {
                                      setEditando(vinculo.id);
                                      setCriando(false);
                                      setErro(null);
                                    }}
                                    className="inline-flex size-[26px] items-center justify-center rounded-md border border-border bg-dominant text-ink-soft hover:border-accent disabled:opacity-40"
                                  >
                                    <Pencil size={13} aria-hidden="true" />
                                  </button>
                                  <button
                                    type="button"
                                    disabled={ocupado}
                                    title="Remover"
                                    aria-label={`Remover o vínculo da ${fornecedor.apelido}`}
                                    onClick={() => remover.mutate(vinculo.id)}
                                    className="inline-flex size-[26px] items-center justify-center rounded-md border border-border bg-dominant text-danger hover:border-danger disabled:opacity-40"
                                  >
                                    <Trash2 size={13} aria-hidden="true" />
                                  </button>
                                </span>
                              </td>
                            )}
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * O código que o fornecedor daquela marca dá a este item, nesta cor.
 *
 * O sistema preenche sozinho ao ler as propostas, cruzando produto, cor e
 * marca. Quando não acha — linha sem cor no PDF, ou cor que o fornecedor vende
 * e a Condusvale não cadastrou naquela marca — o comprador escreve aqui, uma
 * vez, e o sistema para de perguntar.
 */
function CodigoDoFornecedor({
  item,
  fornecedores,
  podeCadastrar,
  escrevendo,
  ocupado,
  aoAbrir,
  aoFechar,
  aoSalvar,
}: {
  item: ItemErp;
  fornecedores: { id: number; apelido: string; marca_erp: string | null }[];
  podeCadastrar: boolean;
  escrevendo: boolean;
  ocupado: boolean;
  aoAbrir: () => void;
  aoFechar: () => void;
  aoSalvar: (fornecedorId: number, codigo: string) => void;
}) {
  // A marca do item diz de quem é o código: o 11148 é o Atox Solar preto "SIL".
  const daMarca = fornecedores.find((f) => f.marca_erp && f.marca_erp === item.marca);
  const [codigo, setCodigo] = useState(item.codigos_fornecedor[0]?.codigo ?? '');
  // Zero enquanto ninguém escolheu: a lista de fornecedores chega depois da
  // tabela, e fixar o padrão no estado inicial guardaria uma escolha vazia.
  const [escolhido, setEscolhido] = useState(0);
  const fornecedorId =
    escolhido || item.codigos_fornecedor[0]?.fornecedor_id || daMarca?.id || fornecedores[0]?.id || 0;

  if (escrevendo) {
    return (
      <form
        className="flex items-center gap-xs"
        onSubmit={(evento) => {
          evento.preventDefault();
          aoSalvar(fornecedorId, codigo.trim());
        }}
      >
        {!daMarca && (
          <select
            value={fornecedorId}
            onChange={(evento) => setEscolhido(Number(evento.target.value))}
            className="rounded-md border border-border bg-dominant px-xs py-[2px] text-[12px] text-ink"
          >
            {fornecedores.map((fornecedor) => (
              <option key={fornecedor.id} value={fornecedor.id}>
                {fornecedor.apelido}
              </option>
            ))}
          </select>
        )}
        <input
          type="text"
          autoFocus
          maxLength={40}
          value={codigo}
          onChange={(evento) => setCodigo(evento.target.value)}
          placeholder="Código dele"
          className="w-[130px] rounded-md border border-border bg-dominant px-xs py-[2px] text-[12px] text-ink placeholder:text-muted focus:border-accent focus:outline-none"
        />
        <button
          type="submit"
          disabled={ocupado || !codigo.trim()}
          className="inline-flex size-[22px] items-center justify-center rounded-md border border-border bg-surface text-ink-soft hover:border-accent disabled:opacity-40"
        >
          <Check size={12} aria-hidden="true" />
          <span className="sr-only">Gravar</span>
        </button>
        <button
          type="button"
          onClick={aoFechar}
          className="inline-flex size-[22px] items-center justify-center rounded-md border border-border bg-surface text-muted hover:border-accent"
        >
          <X size={12} aria-hidden="true" />
          <span className="sr-only">Cancelar</span>
        </button>
      </form>
    );
  }

  if (item.codigos_fornecedor.length === 0) {
    return podeCadastrar ? (
      <button type="button" onClick={aoAbrir} className="text-[12px] text-accent hover:underline">
        {daMarca ? `Informar o código da ${daMarca.apelido}` : 'Informar o código'}
      </button>
    ) : (
      <span className="text-muted">—</span>
    );
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-xs">
      {item.codigos_fornecedor.map((codigoDele) => (
        <span
          key={codigoDele.id}
          title={explicar(codigoDele)}
          className={`inline-flex items-center gap-xs rounded-md border bg-dominant px-xs py-[2px] tabular-nums ${
            codigoDele.situacao === 'diverge' ? 'border-warning/50' : 'border-border'
          }`}
        >
          {!daMarca && <span className="text-muted">{codigoDele.apelido}</span>}
          {codigoDele.codigo_pedido ?? '—'}
          {codigoDele.situacao === 'diverge' && (
            <AlertCircle size={11} className="text-warning" aria-hidden="true" />
          )}
          {codigoDele.confirmado_em && <BadgeCheck size={11} className="text-success" aria-hidden="true" />}
        </span>
      ))}

      {podeCadastrar && (
        <button
          type="button"
          onClick={aoAbrir}
          title="Corrigir o código do fornecedor"
          className="inline-flex size-[20px] items-center justify-center rounded-md text-muted hover:text-accent"
        >
          <Pencil size={11} aria-hidden="true" />
          <span className="sr-only">Corrigir o código do fornecedor</span>
        </button>
      )}
    </span>
  );
}

/**
 * A frase que aparece ao parar o mouse sobre o código. É onde o comprador
 * descobre por que um código tem sufixo que o orçamento não tinha, ou por que
 * há um triângulo amarelo ao lado dele.
 */
function explicar(codigo: CodigoDeFornecedor): string {
  switch (codigo.situacao) {
    case 'diverge':
      return `O orçamento diz ${codigo.codigo}; o cadastro da Condusvale diz ${codigo.codigo_cadastro}. Vale o do orçamento.`;
    case 'completa':
      return `Do orçamento veio ${codigo.codigo}; o cadastro completa a cor e a embalagem: ${codigo.codigo_cadastro}.`;
    case 'so_cadastro':
      return 'Do cadastro da Condusvale — nenhum orçamento deste fornecedor cotou este item ainda.';
    case 'confere':
      return `O orçamento e o cadastro dizem o mesmo: ${codigo.codigo}.`;
    default:
      return codigo.descricao ?? '';
  }
}

/** De onde o vínculo veio e se alguém já olhou para ele. */
function Selo({ vinculo }: { vinculo: Vinculo }) {
  if (vinculo.confirmado_em) {
    return (
      <span className="inline-flex items-center gap-xs whitespace-nowrap text-success">
        <BadgeCheck size={12} aria-hidden="true" />
        Conferido{vinculo.confirmado_por ? ` por ${vinculo.confirmado_por}` : ''} em{' '}
        {vinculo.confirmado_em.slice(0, 10).split('-').reverse().join('/')}
      </span>
    );
  }

  return (
    <span className="text-muted">
      {vinculo.origem === 'manual' ? 'Escrito à mão' : 'Aprendido de uma proposta, sem conferir'}
    </span>
  );
}
